import { CoreFieldKey, CoreFieldsMapping, getFavouriteShotFromEnum } from 'forms/my-account'
import { ADDRESS_PRIORITY_LOOKUP, FIRST_TOURNAMENT_DATE_FALLBACK } from 'constants/profile'
import i18n from 'constants/strings'
import {
  FeatureStatusEnum,
  MatchUpTypeEnum,
  PlayingDoubleHandEnum,
  SideEnum,
} from 'types/api/enums'
import { Address, Features, MatchUp, WatchlistAPI } from 'types/api/types'
import {
  VsInfo,
  PlayerStats,
  CommonMatch,
  Player,
  MatchUpsWinLoss,
  MatchUpStatBucket,
  SetUI,
  MatchUpWorldTennisNumber,
  WorldTennisNumberTypeEnum,
  Tournament,
  MatchUpUI,
  SocialMatchUpUI,
  SidesUI,
  Watchlist,
  PlayerLocationUI,
  AddressUI,
  RatingHistory,
  PsqResults,
  WorldTennisNumber,
  GameZoneBounds,
  VsWinLikelihood,
  GetVsInfoQueryResponse,
} from 'types/global'
import { roundDownWtn, getUnknownDisplayText } from 'utils'
import { ScoreType } from 'forms/post-score/types'

const getFullName = (first: string, last: string): string | undefined => {
  if (first && last) {
    return `${first} ${last}`.toLowerCase()
  } else if (first || last) {
    return (first || last).toLowerCase()
  }
}

const getGameZoneValues = (wtn: WorldTennisNumber) => ({
  lower: +roundDownWtn(wtn.gameZoneLower),
  upper: +roundDownWtn(wtn.gameZoneUpper),
})

export const getWtns = (ratingsArr: WorldTennisNumber[]) => {
  const ratings: {
    wtnSingles?: string
    wtnConfidenceSingles?: number
    wtnSinglesNumeric?: number | null
    wtnDoubles?: string
    wtnConfidenceDoubles?: number
    wtnDoublesNumeric?: number | null
    gameZoneSingles?: GameZoneBounds
    gameZoneDoubles?: GameZoneBounds
  } = {}
  // Player is ranked for either Singles or Doubles
  if (ratingsArr === null) return { wtnSingles: '', wtnDoubles: '' }

  if (ratingsArr && ratingsArr.length) {
    const singles = ratingsArr.find(r => r.type === WorldTennisNumberTypeEnum.SINGLE)
    const doubles = ratingsArr.find(r => r.type === WorldTennisNumberTypeEnum.DOUBLE)

    if (singles) {
      const hasGameZone = Boolean(singles.gameZoneLower || singles.gameZoneUpper)
      ratings.wtnSingles = singles.tennisNumber ? roundDownWtn(singles.tennisNumber) : ''
      ratings.wtnConfidenceSingles = singles.confidence
      ratings.wtnSinglesNumeric = singles.tennisNumber ?? null
      ratings.gameZoneSingles = hasGameZone ? getGameZoneValues(singles) : undefined
    } //WTN latest requirement - round down to 1 decimal place.
    if (doubles) {
      const hasGameZone = Boolean(doubles.gameZoneLower || doubles.gameZoneUpper)
      ratings.wtnDoubles = doubles.tennisNumber ? roundDownWtn(doubles.tennisNumber) : ''
      ratings.wtnConfidenceDoubles = doubles.confidence
      ratings.wtnDoublesNumeric = doubles.tennisNumber ?? null
      ratings.gameZoneDoubles = hasGameZone ? getGameZoneValues(doubles) : undefined
    }
  }
  return ratings
}

export const sortRatingsByType = ratingsArr => {
  const ratings: RatingHistory = {
    SINGLES: [],
    DOUBLES: [],
  }
  if (ratingsArr && ratingsArr.length) {
    const singles = ratingsArr.filter(r => r.type === WorldTennisNumberTypeEnum.SINGLE)
    const doubles = ratingsArr.filter(r => r.type === WorldTennisNumberTypeEnum.DOUBLE)
    if (singles) ratings.SINGLES = singles
    if (doubles) ratings.DOUBLES = doubles
  }
  return ratings
}

export const getGender = (genderChar): string | undefined => {
  switch (genderChar) {
    case 'F':
      return i18n.t('strData.female')
    case 'M':
      return i18n.t('strData.male')
    case 'U':
      return i18n.t('strData.unspecified')
  }
}

export const getBackhand = (backhand): string => {
  switch (backhand) {
    case 'NO':
      return i18n.t('strData.single')
    case 'BH':
      return i18n.t('strData.double')
    case 'FB':
      return i18n.t('strData.double')
    case 'FH':
      return i18n.t('strData.double')
    default:
      return getUnknownDisplayText()
  }
}

export const getDoublePlayingHandFromBackhand = (backhand: string): PlayingDoubleHandEnum => {
  switch (backhand) {
    case i18n.t('strData.single'):
      return PlayingDoubleHandEnum.NO
    case i18n.t('strData.double'):
      return PlayingDoubleHandEnum.BH
    default:
      return PlayingDoubleHandEnum.U
  }
}

export const getPreferredHand = (hand): string => {
  switch (hand) {
    case 'L':
      return i18n.t('strData.left')
    case 'R':
      return i18n.t('strData.right')
    case 'A':
      return i18n.t('strData.ambidextrous')
    default:
      return getUnknownDisplayText()
  }
}

export const getPlayingHandFromPreferred = (hand: string): string => {
  switch (hand) {
    case i18n.t('strData.left'):
      return 'L'
    case i18n.t('strData.right'):
      return 'R'
    case i18n.t('strData.ambidextrous'):
      return 'A'
    default:
      return 'U'
  }
}

export const getLocation = (
  addresses: Address[] | null | undefined
): PlayerLocationUI | undefined => {
  if (!addresses || !addresses.length) return undefined
  const sanitized: AddressUI[] = addresses
    .filter(addr => !!addr)
    .map(({ city, countryCode, state, addressType }) => ({
      addressType: addressType ?? '',
      city: city ?? '',
      countryCode: countryCode ?? '',
      state: state ?? '',
    }))
  const [main, ...secondary] = sanitized.sort((a, b) => {
    const priorityA = ADDRESS_PRIORITY_LOOKUP[a.addressType] ?? 100
    const priorityB = ADDRESS_PRIORITY_LOOKUP[b.addressType] ?? 100
    return priorityA - priorityB
  })

  return {
    main,
    secondary: secondary.length ? secondary : undefined,
  }
}

export const transformPlayer = (gqlPlayer): Player => {
  const {
    nativeGivenName,
    nativeFamilyName,
    standardGivenName,
    standardFamilyName,
    nationalityCode,
    birthDate,
    age,
    worldTennisNumbers,
    matchUps,
    sex,
    countryOfResidence,
    preferredHand,
    backhand,
    favoriteShot,
    nickname,
    addresses,
    ...rest
  } = gqlPlayer?.person ? gqlPlayer.person : gqlPlayer || {}

  const {
    playingHand,
    doublePlayingHand,
    heightInMeters,
    avatarUrl,
    favouriteShot,
    residenceCountryCode,
  } = gqlPlayer?.person
    ? gqlPlayer.person?.biographicalInformation || {}
    : gqlPlayer?.biographicalInformation || {}

  return {
    ...rest,
    totalMatchUps: matchUps?.totalItems,
    fullName: getFullName(nativeGivenName, nativeFamilyName),
    firstName: nativeGivenName,
    lastName: nativeFamilyName,
    countryCode: nationalityCode,
    age,
    sex,
    birthDate,
    avatar: avatarUrl,
    gender: getGender(sex),
    residence: residenceCountryCode,
    preferredHand: getPreferredHand(playingHand),
    backhand: getBackhand(doublePlayingHand),
    height: heightInMeters,
    favouriteShot: getFavouriteShotFromEnum(favouriteShot),
    nickname,
    location: getLocation(addresses),
    ...getWtns(worldTennisNumbers),
  }
}

const addContemporaryRatingToPlayer: (
  a: Player,
  b: MatchUpWorldTennisNumber[] | undefined,
  c?: WorldTennisNumberTypeEnum | MatchUpTypeEnum
) => Player = (player, ratings, ratingType = WorldTennisNumberTypeEnum.SINGLE) => {
  const cr =
    ratings &&
    ratings.find(r => player?.id === r.personId && checkIfCorrectMatchType(r.type, ratingType))

  if (cr) {
    player.contemporaryWtn = cr.tennisNumber ? roundDownWtn(cr.tennisNumber) : ''
    player.contemporaryWtnConfidence = cr.confidence
  }

  return player
}

export const transformMatchUpForProfile = <T extends MatchUpUI | SocialMatchUpUI>(
  match: MatchUp
): T | null => {
  if (!match?.sides || !match?.sides?.length) return null

  const {
    id,
    type,
    start,
    status,
    score,
    worldTennisNumbers,
    ownerId,
    submissionState,
    reviewedBy,
    updatedAt,
    createdAt,
  } = match
  let { superTiebreak, sets } = score || { sets: [] } // BYE matches do not have a score
  let winningSide = match.winningSide || SideEnum.SIDE1
  let losingSide = winningSide === SideEnum.SIDE1 ? SideEnum.SIDE2 : SideEnum.SIDE1
  let sides: SidesUI = {
    SIDE1: [],
    SIDE2: [],
  }

  match.sides.forEach((side, i) => {
    const { players, sideNumber } = side || {}

    sides[sideNumber] = players
      ? addContemporaryRatingToPlayers(transformPlayers(players), worldTennisNumbers, type)
      : []
  })

  const setsUI: SetUI[] = sets.map(set => {
    let type: ScoreType = set.tiebreaker && set.winnerGamesWon === null ? 'tiebreak' : 'set'
    if (type === 'tiebreak') {
      return {
        type,
        points: {
          [winningSide]: set.tiebreaker.winnerPointsWon,
          [losingSide]: set.tiebreaker.loserPointsWon,
        },
      } as SetUI
    }

    return {
      type,
      points: {
        [winningSide]: set.winnerGamesWon,
        [losingSide]: set.loserGamesWon,
      },
      tiebreaker: set.tiebreaker
        ? {
            type: 'tiebreak',
            points: {
              [winningSide]: set.tiebreaker.winnerPointsWon,
              [losingSide]: set.tiebreaker.loserPointsWon,
            },
          }
        : undefined,
    } as SetUI
  })

  if (superTiebreak)
    setsUI.push({
      type: 'superTiebreak',
      points: {
        [winningSide]: superTiebreak.winnerPointsWon,
        [losingSide]: superTiebreak.loserPointsWon,
      },
    } as SetUI)

  return {
    id,
    type,
    status,
    start,
    sets: setsUI,
    sides,
    winningSide: match.winningSide,
    submittedById: ownerId,
    submittedStatus: submissionState,
    reviewedById: reviewedBy?.id,
    updatedAt: updatedAt || '',
    createdAt,
  } as T
}

function checkIfCorrectMatchType(
  type: WorldTennisNumberTypeEnum | 'DOUBLES' | 'SINGLES',
  ratingType: WorldTennisNumberTypeEnum | MatchUpTypeEnum | 'DOUBLES' | 'SINGLES'
) {
  if (type === 'SINGLES' || type === WorldTennisNumberTypeEnum.SINGLE) {
    return ratingType === 'SINGLES' || ratingType === 'SINGLE'
  }
  if (type === 'DOUBLES' || type === WorldTennisNumberTypeEnum.DOUBLE) {
    return ratingType === 'DOUBLES' || ratingType === 'DOUBLE'
  }

  return false
}

const addContemporaryRatingToPlayers: (
  a: Player[],
  b: MatchUpWorldTennisNumber[] | undefined,
  c?: WorldTennisNumberTypeEnum | MatchUpTypeEnum
) => Player[] = (players, ratings, ratingType = WorldTennisNumberTypeEnum.SINGLE) => {
  return players.map(p => addContemporaryRatingToPlayer(p, ratings, ratingType))
}

export const transformMatchResults = (tournaments, playerId) => {
  const tournamentRes = {}

  if (tournaments) {
    tournaments.items.forEach((t, i) => {
      // we need to hold the null tournaments in state, so we can calculate an accurate total when paginating queries
      const key = t?.id ?? 'NULL' + i
      // Populate Tournament information
      tournamentRes[key] = {
        id: t?.id,
        name: t?.name,
        start: t?.start,
        end: t?.end,
        results: [],
        provider: t?.provider,
        providerTournamentId: t?.providerTournamentId,
      }

      const allMatches = t?.matchUps?.items

      if (allMatches) {
        allMatches.forEach(r => {
          const matchUp = transformMatchUpForProfile<MatchUpUI>(r)
          if (matchUp && t?.id)
            tournamentRes[t?.id].results = [...tournamentRes[t?.id].results, matchUp]
        })
      }
    })
  }
  return {
    listings: Object.values<any>(tournamentRes),
    totalTournaments: tournaments?.totalItems || 0,
  }
}

export const transformSocialMatches = matches => {
  let socialMatches: SocialMatchUpUI[] = []
  matches?.forEach(m => {
    const socialMatch = transformMatchUpForProfile<SocialMatchUpUI>(m)
    if (socialMatch) socialMatches.push(socialMatch)
  })
  return socialMatches
}

export const transformCommonMatches = (matches): CommonMatch[] => {
  if (!matches) return []

  return matches.map(({ winningSide, score, sides, tournament, worldTennisNumbers, ...rest }) => {
    const winner = winningSide === 'SIDE1' ? sides[0].players[0] : sides[1].players[0]

    return {
      tournament: tournament?.name,
      winner: addContemporaryRatingToPlayer(transformPlayer(winner), worldTennisNumbers),
      score,
      ...rest,
    }
  })
}

const getWinLikelihood = (winLikelihood: number | null): [VsWinLikelihood, VsWinLikelihood] => {
  if (typeof winLikelihood === 'number')
    // win likelihood can be -1, which means the confidence of one or both players' WTN is below 70%,
    // in which case we want both to have -1
    return [
      { singles: winLikelihood },
      { singles: winLikelihood >= 0 ? 100 - winLikelihood : winLikelihood },
    ]

  return [{}, {}]
}

export const transformVsInfo = (info: GetVsInfoQueryResponse, requestIds: string[]): VsInfo => {
  try {
    const vsCounts = info.matchUpStatistics?.matchUpVsStatisticsCount
    const [p1, p2] = requestIds.map(id =>
      info.persons.items.map(transformPlayer).find(p => p.id === id)
    ) as [Player, Player]

    return {
      players: [p1, p2],
      competedTotal: vsCounts.totalMatches,
      wins: { p1: vsCounts.wins ?? 0, p2: vsCounts.losses ?? 0 },
      winLikelihood: getWinLikelihood(info?.matchUpStatistics?.winLikelihood),
    }
  } catch (e) {
    throw Error('missing players in vsInfo')
  }
}

export const transformCommonCompetitorMatches = info => {
  return info.map(({ p1Vs, p2Vs, ...player }) => ({
    ...transformPlayer(player),
    p1Win: p1Vs.wins,
    p1Loss: p1Vs.losses,
    p2Win: p2Vs.wins,
    p2Loss: p2Vs.losses,
  }))
}

export const transformPlayers = (gqlPlayers: any[]) => {
  return gqlPlayers && gqlPlayers.map(transformPlayer)
}

export const transformResultCounts = (resultCounts: any = {}) => {
  const { singles, doubles, both } = resultCounts

  return {
    singles: transformResultCount(singles),
    doubles: transformResultCount(doubles),
    both: transformResultCount(both),
  }
}

export const transformResultCount = (resultCount: any = {}) => {
  const { totalMatches, matchesWon, matchesLost } = resultCount || {}

  return { totalMatches, matchesWon, matchesLost }
}

export const transformPlayerStats = (playerStats: any = {}): PlayerStats => {
  try {
    const { __typename, singlesInterval, doublesInterval, id, ...rest } = playerStats.person
    const { uniqueOpponents } = playerStats.matchUpAggregatedCount

    return {
      playerId: id,
      singlesInterval: singlesInterval?.avgDays,
      doublesInterval: doublesInterval?.avgDays,
      uniqueOpponents: uniqueOpponents?.count,
      ...rest,
    }
  } catch (e) {
    console.log('unable to transform playerStats', e && (e as Error).message)
    return {}
  }
}

export const transformMatchUpsWinLoss = (matchUps: any = {}, playerId): MatchUpsWinLoss[] => {
  const isWinner = (playerId: string, matchItem): Boolean | null => {
    const winningSide = matchItem.sides.filter(x => x.sideNumber === matchItem.winningSide) //filter to get winning side
    if (winningSide.length) {
      return winningSide[0].players.some(x => x.person.id === playerId)
    } else {
      return null
    }
  }

  if (matchUps && matchUps.items) {
    return matchUps.items.map(m => ({
      id: m.id,
      start: m.start,
      wins: isWinner(playerId, m) === true ? 1 : 0,
      losses: isWinner(playerId, m) === false ? 1 : 0,
    }))
  } else {
    return []
  }
}

export const transformMatchUpBuckets = (buckets): MatchUpStatBucket[] => {
  let matchUpBuckets = []

  buckets.forEach(bucket => {
    matchUpBuckets = matchUpBuckets.concat(bucket.matchUpStatsBuckets)
  })

  return matchUpBuckets
}

export const transformPsq = (psq: WorldTennisNumber[]): PsqResults => {
  return {
    wtnSingles: psq.find(psq => psq.type === 'SINGLE')?.tennisNumber,
    wtnDoubles: psq.find(psq => psq.type === 'DOUBLE')?.tennisNumber,
  }
}

export const getFirstTournamentDate = (tournaments: Tournament[]) => {
  if (tournaments && tournaments.length) {
    for (let i = 0; i < tournaments.length; i++) {
      if (tournaments[i].start) return tournaments[i].start
    }
  }
  return FIRST_TOURNAMENT_DATE_FALLBACK
}

export const getEditableFields = input => {
  let editableFields: CoreFieldKey[] = []
  const keys = Object.keys(CoreFieldsMapping)
  keys.forEach(k => input[k] && editableFields.push(CoreFieldsMapping[k]))
  return editableFields
}

export const transformWatchlist = (watchlist: WatchlistAPI): Watchlist => {
  const { id, personIds, name, persons, watchlistStats } = watchlist

  return {
    id,
    name: name ?? '',
    playerIds: personIds ?? [],
    players: persons ? transformPlayers(persons) : [],
    stats: {
      avgSinglesWtn: watchlistStats?.meanSinglesWorldTennisNumber
        ? roundDownWtn(watchlistStats?.meanSinglesWorldTennisNumber)
        : '',
      minSinglesWtn: watchlistStats?.maximumSinglesWorldTennisNumber
        ? roundDownWtn(watchlistStats?.maximumSinglesWorldTennisNumber)
        : '',
      maxSinglesWtn: watchlistStats?.minimumSinglesWorldTennisNumber
        ? roundDownWtn(watchlistStats?.minimumSinglesWorldTennisNumber)
        : '',
    },
  }
}

export const transformAppFeatures = (features: Features) => {
  const { walkUpRegistration } = features ?? {}

  return {
    canRegisterThroughWalkUp: walkUpRegistration === FeatureStatusEnum.ENABLED,
  }
}
